export type Shape = 'circle' | 'square' | 'diamond' | 'star';

export interface IconOptions {
  shape: 'circle' | 'square' | 'diamond' | 'star';
  fillColor: string;
  strokeColor: string;
  text: string;
  fontSize: number;
  size: number;
  r: number;
}

export interface ShapeLogoOptions {
  shape: Shape;
  size: number;
  r: number;
  shapeFillColor: string;
  borderColor: string;
  borderWidth: number;
  textColor: string;
  fontSize: number;
  label?: string;
}

/**
 * Draws an n‑pointed star on the given 2D context.
 * @param ctx      CanvasRenderingContext2D
 * @param cx       center x
 * @param cy       center y
 * @param outerR   outer radius (tips)
 * @param innerR   inner radius (indent points)
 * @param points   number of star points
 */
function drawStar(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  points: number
) {
  const step = Math.PI / points;
  ctx.beginPath();
  for (let i = 0; i < 2 * points; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const a = i * step - Math.PI / 2;
    ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
  }
  ctx.closePath();
}

export function generateShapeLogo(opts: ShapeLogoOptions): Promise<File> {
  return new Promise((resolve, reject) => {
    const { shape, size, r, shapeFillColor, borderColor, borderWidth, textColor, fontSize, label } = opts;

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const cx = size / 2,
      cy = size / 2;

    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = shapeFillColor;
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = borderWidth;

    ctx.beginPath();
    switch (shape) {
      case 'circle':
        ctx.arc(cx, cy, r, 0, 2 * Math.PI);
        break;
      case 'square':
        ctx.rect(cx - r, cy - r, 2 * r, 2 * r);
        break;
      case 'diamond':
        ctx.moveTo(cx, cy - r);
        ctx.lineTo(cx + r, cy);
        ctx.lineTo(cx, cy + r);
        ctx.lineTo(cx - r, cy);
        ctx.closePath();
        break;
      case 'star':
        const spikes = 5;
        const outer = r;
        const inner = r * 0.5;
        let rot = (Math.PI / 2) * 3;
        let x = cx,
          y = cy;
        const step = Math.PI / spikes;
        ctx.moveTo(cx, cy - outer);
        for (let i = 0; i < spikes; i++) {
          x = cx + Math.cos(rot) * outer;
          y = cy + Math.sin(rot) * outer;
          ctx.lineTo(x, y);
          rot += step;

          x = cx + Math.cos(rot) * inner;
          y = cy + Math.sin(rot) * inner;
          ctx.lineTo(x, y);
          rot += step;
        }
        ctx.closePath();
        break;
    }
    ctx.fill();
    if (borderWidth > 0) ctx.stroke();

    if (label) {
      ctx.fillStyle = textColor;
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, cx, cy);
    }

    canvas.toBlob(blob => {
      if (!blob) return reject(new Error('Failed to generate logo'));
      resolve(new File([blob], 'logo.png', { type: 'image/png' }));
    }, 'image/png');
  });
}

export function generateColorLogo(colors: string[], size = 200, filename = 'logo.png', label?: string): Promise<File> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;

    const cx = size / 2;
    const cy = size / 2;
    const r = size / 2;

    ctx.clearRect(0, 0, size, size);

    if (colors.length === 0) {
      const strokeWidth = 1;
      const radius = r - strokeWidth / 2;

      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, 2 * Math.PI);
      ctx.fill();

      ctx.strokeStyle = '#CCCCCC';
      ctx.lineWidth = strokeWidth;
      ctx.stroke();

      if (label) {
        ctx.fillStyle = '#444444';
        ctx.font = `${Math.floor(radius * 1.2)}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label.toUpperCase(), cx, cy);
      }
    } else if (colors.length === 1) {
      ctx.fillStyle = colors[0];
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, 2 * Math.PI);
      ctx.fill();
    } else {
      const startAngle = -Math.PI / 4;
      const midAngle = startAngle + Math.PI;

      ctx.fillStyle = colors[0];
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, startAngle, midAngle);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = colors[1];
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, midAngle, startAngle + 2 * Math.PI);
      ctx.closePath();
      ctx.fill();
    }

    canvas.toBlob(blob => {
      if (!blob) return reject(new Error('Failed to generate logo blob'));
      const file = new File([blob], filename, { type: 'image/png' });
      resolve(file);
    }, 'image/png');
  });
}
